import type { CSSProperties, RefObject } from 'react';
import { memo, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ChatSyncIndicator } from '../ChatSyncIndicator';
import type { Message } from '../chat-types';
import { MessageBubble } from '../MessageBubble';
import { ReasoningTrace } from '../ReasoningTrace';
import { ToolExecution } from '../tool-execution/ToolExecution';
import type { BlurRegion, ChatLayout, MessageListRegistrations, SuggestionSelectHandler } from './types';
import { computeBlurMaskStyle, filterRenderableMessages } from './utils';

interface MessageListProperties extends MessageListRegistrations {
  blurRegion: BlurRegion | null;
  layout: ChatLayout;
  messages: Message[];
  messagesEndRef: RefObject<HTMLDivElement | null>;
  onSuggestionSelect?: SuggestionSelectHandler | undefined;
  paddingStyle: CSSProperties;
  powerUserEnabled?: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  showSyncIndicator: boolean;
}

export const MessageList = memo(function MessageListComponent({
  messages,
  layout,
  powerUserEnabled = false,
  scrollContainerRef,
  messagesEndRef,
  paddingStyle,
  blurRegion,
  showSyncIndicator,
  registerWrapper,
  registerMessageElement,
  onSuggestionSelect,
}: MessageListProperties) {
  const items = useMemo(() => filterRenderableMessages(messages), [messages]);
  const blurMaskStyle = useMemo(() => computeBlurMaskStyle(blurRegion), [blurRegion]);
  const isSidebarLayout = layout === 'sidebar';

  return (
    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-0 pb-4 sm:pb-6" style={paddingStyle}>
      <div
        ref={registerWrapper}
        className={cn('relative flex flex-col gap-3 pb-2 sm:pb-3', isSidebarLayout ? 'px-4' : undefined)}
      >
        {blurRegion ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 z-layer-base overflow-visible rounded-[36px] transition-[height,top,opacity] duration-300 ease-out"
            style={{ top: blurRegion.top, height: blurRegion.height }}
          >
            <div
              className="h-full w-full rounded-[36px] bg-transparent shadow-[030px_90px_-48px_rgba(15,23,42,0.55)] supports-[backdrop-filter]:backdrop-blur-[34px]"
              style={blurMaskStyle}
            />
          </div>
        ) : null}

        {items.map((message) => (
          <div
            key={message.id}
            ref={registerMessageElement(message.id)}
            className="chat-message-enter relative z-layer-surface"
          >
            <MessageBubble
              message={message}
              layout={layout}
              powerUserEnabled={powerUserEnabled}
              onSuggestionSelect={onSuggestionSelect}
            />
            {message.role === 'assistant' && message.reasoningTrace && message.reasoningTrace.length > 0 ? (
              <ReasoningTrace entries={message.reasoningTrace} className="mt-2" />
            ) : null}
            {message.toolInvocations && message.toolInvocations.length > 0 ? (
              <div className="mt-2 space-y-2">
                {message.toolInvocations.map((invocation) => (
                  <ToolExecution
                    key={invocation.toolCallId}
                    toolName={invocation.toolName}
                    args={invocation.args}
                    state={invocation.state}
                    result={invocation.result ?? null}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ))}

        {showSyncIndicator ? (
          <div className="relative z-layer-surface">
            <ChatSyncIndicator className="mx-auto" />
          </div>
        ) : null}
        <div ref={messagesEndRef} className="relative z-layer-surface h-px" />
      </div>
    </div>
  );
});
